import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axiosInstance from "../../../../../redux/api";
import { getAllOccassion } from "../../../../../redux/occassion/action";
import { getAllBrands } from "../../../../../redux/brand/action";
import {
  getAllCategorieslist,
  getAllSize,
} from "../../../../../redux/size/action";
import { getAllColor } from "../../../../../redux/color/action";
import { getAllProductList } from "../../../../../redux/Product/action";

const LIST_PARAMS = { page: 1, search_key: "", status: "" };
const CATEGORY_PARAMS = { ...LIST_PARAMS };

// Scoped options are stored together with the selection key they were fetched
// for, so "loading" can be DERIVED during render (key mismatch = not loaded
// yet). Stable empty references avoid re-render churn while loading.
const EMPTY_OPTIONS = [];
const EMPTY_SCOPED_DATA = { key: "", options: EMPTY_OPTIONS };

const toOption = (item) => ({ value: item?.id, label: item?.title ?? item?.name });

const idListParamsSerializer = (params) =>
  Object.entries(params)
    .flatMap(([key, val]) =>
      Array.isArray(val)
        ? val.map((v) => `${key}=${encodeURIComponent(v)}`)
        : [`${key}=${encodeURIComponent(val)}`],
    )
    .join("&");

/**
 * One hook owns loading and shaping every dropdown option the product form needs.
 * Call it once at the shell level — consumers get a stable, memoized payload.
 *
 * @param {object} args
 * @param {number[]} args.selectedCategoryIds    - currently selected categories, drives
 *                                                 the subcategory option fetch
 * @param {number[]} args.selectedSubCategoryIds - currently selected subcategories, drives
 *                                                 the type-by-subcategory fetch
 * @param {number} [args.excludeProductId]       - product id to exclude from "related" list
 */
export const useProductFormOptions = ({
  selectedCategoryIds = [],
  selectedSubCategoryIds = [],
  excludeProductId,
} = {}) => {
  const dispatch = useDispatch();

  /* ---- one-shot list fetches (brands, categories, colors, sizes, occasions, products) ---- */
  useEffect(() => {
    dispatch(getAllBrands(LIST_PARAMS));
    dispatch(getAllColor(LIST_PARAMS));
    dispatch(getAllSize(LIST_PARAMS));
    dispatch(getAllOccassion(LIST_PARAMS));
    dispatch(getAllCategorieslist(CATEGORY_PARAMS));
    dispatch(getAllProductList(LIST_PARAMS));
  }, [dispatch]);

  const brandList = useSelector((s) => s.reusableSlice.brandList);
  const colorList = useSelector((s) => s.reusableSlice.colorListData);
  const sizeList = useSelector((s) => s.reusableSlice.sizeListData);
  const occasionList = useSelector((s) => s.reusableSlice.occassionList);
  const categoryList = useSelector((s) => s.reusableSlice.catagoriesList);
  const productList = useSelector((s) => s.reusableSlice.productList);

  /* ---- sub-category fetch driven by category selection, abortable ---- */
  // Sorted stringified key so re-render with the same IDs in different order is a no-op.
  const categoryIdsKey = useMemo(
    () => [...selectedCategoryIds].map(Number).sort((a, b) => a - b).join(","),
    [selectedCategoryIds],
  );

  // Store options alongside the category key they belong to. `isLoading` is
  // then derived from a key mismatch (below) rather than toggled in the effect.
  // This closes a race: OrganizationRail's prune effect runs child-first, i.e.
  // BEFORE this hook's fetch effect in the same commit. A state-based loading
  // flag would still read `false` there and wipe valid sub_category_ids before
  // the fetch even starts. A derived flag is correct synchronously.
  const [subcategoryData, setSubcategoryData] = useState(EMPTY_SCOPED_DATA);
  const subcategoriesRequestRef = useRef(null);

  useEffect(() => {
    if (subcategoriesRequestRef.current) subcategoriesRequestRef.current.abort();

    if (!categoryIdsKey) {
      setSubcategoryData(EMPTY_SCOPED_DATA);
      return;
    }

    const controller = new AbortController();
    subcategoriesRequestRef.current = controller;

    axiosInstance
      .get("admin/sub-categories", {
        params: { ...LIST_PARAMS, category_id: categoryIdsKey },
        signal: controller.signal,
      })
      .then((res) => {
        const items = res.data?.response || [];
        setSubcategoryData({ key: categoryIdsKey, options: items.map(toOption) });
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setSubcategoryData({ key: categoryIdsKey, options: [] });
      });

    return () => controller.abort();
  }, [categoryIdsKey]);

  const subcategoriesReady = subcategoryData.key === categoryIdsKey;
  const subcategoryOptions = subcategoriesReady
    ? subcategoryData.options
    : EMPTY_OPTIONS;
  const isLoadingSubcategories = !!categoryIdsKey && !subcategoriesReady;

  /* ---- types-by-subcategories: fetched on demand, abortable ---- */
  // Types are now scoped to sub-categories, not categories. With no
  // sub-category selected there are no valid types.
  const subCategoryIdsKey = useMemo(
    () => [...selectedSubCategoryIds].map(Number).sort((a, b) => a - b).join(","),
    [selectedSubCategoryIds],
  );

  // Same derived-loading pattern as sub-categories above, for the same reason —
  // the type prune effect in OrganizationRail must not drop a valid type_id
  // before the by-sub-categories fetch has run.
  const [typeData, setTypeData] = useState(EMPTY_SCOPED_DATA);
  const typesRequestRef = useRef(null);

  useEffect(() => {
    // Cancel any in-flight request.
    if (typesRequestRef.current) typesRequestRef.current.abort();

    if (!subCategoryIdsKey) {
      setTypeData(EMPTY_SCOPED_DATA);
      return;
    }

    const controller = new AbortController();
    typesRequestRef.current = controller;

    axiosInstance
      .get("admin/types/by-sub-categories", {
        params: { sub_category_ids: subCategoryIdsKey.split(",") },
        signal: controller.signal,
        paramsSerializer: idListParamsSerializer,
      })
      .then((res) => {
        const types = res.data?.response || [];
        setTypeData({
          key: subCategoryIdsKey,
          options: types.map((t) => ({ value: t.id, label: t.name })),
        });
      })
      .catch((err) => {
        if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setTypeData({ key: subCategoryIdsKey, options: [] });
      });

    return () => controller.abort();
  }, [subCategoryIdsKey]);

  const typesReady = typeData.key === subCategoryIdsKey;
  const typeOptions = typesReady ? typeData.options : EMPTY_OPTIONS;
  const isLoadingTypes = !!subCategoryIdsKey && !typesReady;

  /* ---- memoized options ---- */
  const brandOptions = useMemo(
    () => (Array.isArray(brandList) ? brandList.map(toOption) : []),
    [brandList],
  );
  const colorOptions = useMemo(
    () => (Array.isArray(colorList) ? colorList.map(toOption) : []),
    [colorList],
  );
  const sizeOptions = useMemo(
    () => (Array.isArray(sizeList) ? sizeList.map(toOption) : []),
    [sizeList],
  );
  const occasionOptions = useMemo(
    () => (Array.isArray(occasionList) ? occasionList.map(toOption) : []),
    [occasionList],
  );
  const categoryOptions = useMemo(
    () => (Array.isArray(categoryList) ? categoryList.map(toOption) : []),
    [categoryList],
  );
  const productOptions = useMemo(() => {
    if (!Array.isArray(productList)) return [];
    return productList
      .filter((p) => (excludeProductId ? p?.id !== excludeProductId : true))
      .map((p) => ({
        value: p?.id,
        label: p?.name,
        data: {
          name: p?.name ?? "",
          ar_name: p?.ar_name ?? "",
          image: p?.image ?? "",
          barcode: p?.barcode ?? "",
          size: p?.size ?? null,
          color: p?.color ?? null,
        },
      }));
  }, [productList, excludeProductId]);

  return {
    brandOptions,
    colorOptions,
    sizeOptions,
    occasionOptions,
    categoryOptions,
    subcategoryOptions,
    isLoadingSubcategories,
    productOptions,
    typeOptions,
    isLoadingTypes,
    excludeProductId,
  };
};
